import { Briefcase, Users, Building } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const audiences = [
  {
    icon: Briefcase,
    title: "Consultants & Freelancers",
    description: "Who receive many inbound emails and need to quickly capture contact details.",
    gradient: "from-brand-pink to-brand-orange",
    bgColor: "bg-brand-pink/10",
  },
  {
    icon: Users,
    title: "Sales & Recruiting Teams",
    description: "Working directly from Outlook who need fast, accurate contact data.",
    gradient: "from-brand-orange to-brand-cyan",
    bgColor: "bg-brand-orange/10",
  },
  {
    icon: Building,
    title: "Small Businesses",
    description: "That need clean contacts without CRM complexity or subscription costs.",
    gradient: "from-brand-cyan to-brand-blue",
    bgColor: "bg-brand-cyan/10",
  },
];

const AudienceCard = ({ audience, index }: { audience: typeof audiences[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: 'scale',
    delay: index * 100,
    threshold: 0.15,
  });

  return (
    <div 
      ref={ref}
      className={`group relative ${animationClass}`}
    >
      {/* Gradient Border Effect */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${audience.gradient} rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
      
      {/* Card Content */}
      <div className="relative glass-card-strong rounded-2xl p-6 md:p-8 h-full hover-lift border border-border/50 group-hover:border-border text-center">
        {/* Icon */}
        <div className={`mb-4 md:mb-6 inline-flex p-4 rounded-xl ${audience.bgColor} group-hover:scale-110 transition-transform`}>
          <audience.icon className="w-8 h-8 text-primary" />
        </div>
        
        <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 group-hover:text-primary transition-colors">
          {audience.title}
        </h3>
        
        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
          {audience.description}
        </p>
      </div>
    </div>
  );
};

export const TargetAudience = () => {
  const { ref: headerRef, animationClass: headerAnimation } = useScrollAnimation<HTMLDivElement>({
    type: 'slide-up',
    threshold: 0.3,
  });

  return (
    <section className="py-16 md:py-24 bg-muted/30 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 right-1/4 w-48 md:w-64 h-48 md:h-64 bg-brand-orange/10 rounded-full blur-[100px] animate-blob" />
        <div className="absolute bottom-10 left-1/4 w-48 md:w-64 h-48 md:h-64 bg-brand-cyan/10 rounded-full blur-[100px] animate-blob animation-delay-2000" />
      </div>

      <div className="container px-4 relative z-10">
        <div 
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-10 md:mb-16 ${headerAnimation}`}
        >
          <div className="inline-block px-4 py-2 mb-4 md:mb-6 glass-card rounded-full">
            <span className="text-sm font-semibold bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent">
              Who It's For
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">
            Built for professionals who{" "}
            <span className="bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent">
              live in Outlook.
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-muted-foreground">
            No new system to learn. Works where you already work.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-5xl mx-auto">
          {audiences.map((audience, index) => (
            <AudienceCard key={index} audience={audience} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
